import type { ColumnType, DataTableColDef, ValueOption } from "./types";

// Strings, so filters send the "true" / "false" the backend already reads.
const BOOLEAN_OPTIONS: ValueOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export function optionValue(option: ValueOption): string {
  return typeof option === "string" ? option : option.value;
}

export function optionLabel(option: ValueOption): string {
  return typeof option === "string" ? option : option.label;
}

export function columnType<R>(column: DataTableColDef<R>): ColumnType {
  return column.type ?? "string";
}

export function getCellValue<R>(column: DataTableColDef<R>, row: R): unknown {
  const raw = (row as Record<string, unknown>)[column.field];
  return column.valueGetter ? column.valueGetter(raw, row) : raw;
}

export function formatCellValue<R>(
  column: DataTableColDef<R>,
  value: unknown,
  row: R,
): string {
  if (column.valueFormatter) {
    return column.valueFormatter(value, row);
  }
  if (value === null || value === undefined) {
    return "";
  }
  switch (columnType(column)) {
    case "date":
      return value instanceof Date && !Number.isNaN(value.getTime())
        ? value.toLocaleDateString()
        : String(value);
    case "number":
      return typeof value === "number" ? value.toLocaleString() : String(value);
    case "boolean":
      return value ? "Yes" : "No";
    case "singleSelect": {
      const match = column.valueOptions?.find(
        (option) => optionValue(option) === value,
      );
      return match ? optionLabel(match) : String(value);
    }
    default:
      return String(value);
  }
}

// The row-menu column is never sorted, filtered, hidden or exported, and has no title.
export function withColumnDefaults<R>(
  column: DataTableColDef<R>,
): DataTableColDef<R> {
  if (column.type === "boolean") {
    return { valueOptions: BOOLEAN_OPTIONS, ...column };
  }
  if (column.type !== "actions") return column;
  return {
    width: 75,
    align: "center",
    headerName: "",
    sortable: false,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    disableExport: true,
    ...column,
  };
}
